import axios from 'axios';
import { buildEndpointUrl } from '../../../constants';
import { normalizeTeamKitType } from '../../../constants/teamKitColors';
import type { GameResult } from '../types/gameResults.types';
import { GAME_STATUSES } from '../constants/gameResults.constants';
import { resolveLeagueCountryCodeForFlag } from '../../shared/helpers/leagueCountryToFlagCode.helper';
import { formatLeagueName } from '../../shared/helpers/leagueDisplayName.helper';

type GameStatusValue = (typeof GAME_STATUSES)[keyof typeof GAME_STATUSES];

/**
 * Map API `status` (from GET /matches/results — server reflects DB) to GAME_STATUSES.
 * No client-side kickoff or timeToStart inference; fix wrong display in the sync layer / DB instead.
 */
export function normalizeGameStatus(rawStatus: unknown): GameStatusValue {
  const s = typeof rawStatus === 'string' ? rawStatus.trim().toLowerCase() : '';
  if (s === 'completed' || s === 'finished' || s === 'postponed' || s === 'cancelled' || s === 'abandoned' || s === 'suspended') return GAME_STATUSES.COMPLETED;
  if (s === 'live' || s === 'in_play' || s === 'paused') return GAME_STATUSES.LIVE;
  if (s === 'upcoming' || s === 'scheduled') return GAME_STATUSES.UPCOMING;
  return GAME_STATUSES.UPCOMING;
}

function parseKit(raw: unknown): GameResult['homeKit'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const primary = typeof o.primary === 'string' ? o.primary : undefined;
  const secondary = typeof o.secondary === 'string' ? o.secondary : undefined;
  const type = typeof o.type === 'string' ? o.type : undefined;
  if (!primary || !secondary || !type) return undefined;
  return { primary, secondary, type: normalizeTeamKitType(type) };
}

function normalizeGameResult(raw: Record<string, unknown>): GameResult {
  const timeToStart = typeof raw.timeToStart === 'string' ? raw.timeToStart : '';
  const status = normalizeGameStatus(raw.status);
  const homeKit = parseKit(raw.homeKit);
  const awayKit = parseKit(raw.awayKit);
  const leagueCountryRaw =
    typeof raw.leagueCountry === 'string' ? raw.leagueCountry : undefined;
  const leagueNameRaw = typeof raw.league === 'string' ? raw.league : undefined;
  const leagueCountryCode =
    resolveLeagueCountryCodeForFlag(leagueCountryRaw, leagueNameRaw) ?? undefined;
  const leagueIdRaw = raw.leagueId;
  const leagueId =
    typeof leagueIdRaw === 'string' && leagueIdRaw.trim() !== ''
      ? leagueIdRaw.trim()
      : undefined;

  return {
    id: String(raw.id ?? ''),
    homeTeam: String(raw.homeTeam ?? ''),
    awayTeam: String(raw.awayTeam ?? ''),
    ...(homeKit && { homeKit }),
    ...(awayKit && { awayKit }),
    homeScore: Number(raw.homeScore) || 0,
    awayScore: Number(raw.awayScore) || 0,
    date: String(raw.date ?? ''),
    league: formatLeagueName(typeof raw.league === 'string' ? raw.league : null),
    ...(leagueId && { leagueId }),
    ...(leagueCountryCode && { leagueCountryCode }),
    status,
    timeToStart,
  };
}

/** League option for results filter (no match data). */
export interface LeagueOption {
  id: string;
  name: string;
  /** DB league.country (country name); used for flag. */
  country?: string;
}

/** Optional date/range and timezone for GET /matches/results. */
export interface FetchGameResultsOptions {
  date?: string;
  start?: string;
  end?: string;
  timezone?: string;
}

/** Fetch leagues that have matches (lightweight, for dropdown). */
export const fetchLeaguesForResults = async (): Promise<LeagueOption[]> => {
  try {
    const url = buildEndpointUrl('matches', 'leagues');
    const response = await axios.get<{ success: boolean; data?: LeagueOption[] }>(url);
    if (!response.data?.success || !Array.isArray(response.data.data)) return [];
    return response.data.data;
  } catch {
    return [];
  }
};

export const fetchGameResults = async (
  sortBy: string = 'recent',
  leagueId?: string,
  options?: FetchGameResultsOptions
): Promise<GameResult[]> => {
  try {
    const params = new URLSearchParams();
    if (sortBy !== 'recent') params.set('sortBy', sortBy);
    if (leagueId && leagueId !== 'all') params.set('leagueId', leagueId);
    if (options?.date) params.set('date', options.date);
    if (options?.start) params.set('start', options.start);
    if (options?.end) params.set('end', options.end);
    if (options?.timezone) params.set('timezone', options.timezone);
    const qs = params.toString();
    const url = `${buildEndpointUrl('matches', 'results')}${qs ? `?${qs}` : ''}`;
    const response = await axios.get<{ success: boolean; data?: unknown[] }>(url);

    if (!response.data?.success || !Array.isArray(response.data.data)) {
      return [];
    }
    const normalized = response.data.data
      .map((item) =>
        normalizeGameResult(typeof item === 'object' && item !== null ? (item as Record<string, unknown>) : {})
      )
      .filter((game) => game.id !== '');
    return normalized;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch game results');
  }
};

